/**
 * Service Configurations
 *
 * Centralized configuration for all Maersk shipping services.
 * Each service has its code, ports, and specific settings.
 *
 * @module services/serviceConfigs
 */

/**
 * DCSA v1 Service Codes from Maersk Reference API
 */
export const SERVICE_CODES = {
  SAECS: '278',
  PROTEA: '251',
  SAFARI: '160',
  MESAWA: 'MW1',
  WAFEX: '09D'
};

/**
 * Load Port Configurations for South Africa
 * Used by SAECS, PROTEA, MESAWA, SAFARI (export from SA)
 */
export const SA_LOAD_PORTS = [
  { name: 'Port Elizabeth', portCode: 'ZAPLZ' },
  { name: 'Durban', portCode: 'ZADUR' },
  { name: 'Cape Town', portCode: 'ZACPT' }
];

export const SA_LOAD_PORT_CODES = ['ZAPLZ', 'ZADUR', 'ZACPT'];

/**
 * Discharge Port Configurations
 * Each service has different discharge ports based on route
 */
export const DISCHARGE_PORTS = {
  /**
   * SAECS - South Africa Europe Container Service
   * Route: ZA (Port Elizabeth, Durban, Cape Town) → Europe
   */
  SAECS: [
    { name: 'London Gateway', portCode: 'GBLGP' },
    { name: 'Rotterdam', portCode: 'NLRTM' },
    { name: 'Bremerhaven', portCode: 'DEBRV' },
    {
      name: 'Algeciras - ML',
      portCode: 'ESALG',
      facilitySMDGCode: 'APM' // APM Terminal (not TTI)
    }
  ],

  /**
   * PROTEA - South Africa Indian Ocean Container Service
   * Route: ZA (Durban) → Indian Ocean / Far East
   */
  PROTEA: [
    { name: 'Jebel Ali', portCode: 'AEJEA' },
    { name: 'Mundra', portCode: 'INMUN' },
    { name: 'Jawaharlal Nehru', portCode: 'INNSA' }
  ],

  /**
   * SAFARI - South Africa Far East Service
   * Route: ZA (Durban) → Far East
   */
  SAFARI: [
    { name: 'Port Louis', portCode: 'MUPLU', altCodes: ['MUPOR', 'MUPORT', 'MUPLM', 'MULOU'] },
    { name: 'Tanjung Pelepas', portCode: 'MYTPP', altCodes: ['MYTPG', 'MYTPX', 'MYPEL', 'MYTPEL'] },
    { name: 'Hong Kong', portCode: 'HKHKG', altCodes: ['HKHON', 'HKKAI', 'HKNGK', 'HKONG'] },
    { name: 'Shekou', portCode: 'CNSHK', altCodes: ['CNSKE', 'CNSKX', 'CNSHZ', 'CNSKO'] },
    { name: 'Shanghai', portCode: 'CNSHA', altCodes: ['CNSGH', 'CNSHP', 'CNSHG', 'CNHAI'] },
    { name: 'Ningbo', portCode: 'CNNGB', altCodes: ['CNNIN', 'CNNBO', 'CNNGO', 'CNING'] },
    { name: 'Xiamen', portCode: 'CNXMN', altCodes: ['CNXIA', 'CNXIM', 'CNXME', 'CNXIA'] },
    { name: 'Singapore', portCode: 'SGSIN', altCodes: ['SGSNG', 'SGSIP', 'SGSIN', 'SGPOR'] }
  ],

  /**
   * MESAWA - South Africa Middle East West Africa Service
   * Route: ZA (Cape Town, PE) → Middle East / West Africa
   */
  MESAWA: [
    { name: 'Jebel Ali', portCode: 'AEJEA' },
    { name: 'Mundra', portCode: 'INMUN' },
    { name: 'Jawaharlal Nehru', portCode: 'INNSA' },
    { name: 'Sohar', portCode: 'OMSOH' },
    { name: 'Colombo', portCode: 'LKCMB' }
  ],

  /**
   * WAFEX - South Africa Brazil Service
   * Route: ZA (Durban) → Brazil
   */
  WAFEX: [
    { name: 'Santos', portCode: 'BRSSZ', altCodes: ['BRSAN', 'BRSNT', 'BRSTO', 'BRSAS'] },
    { name: 'Paranagua', portCode: 'BRPNG', altCodes: ['BRPAR', 'BRPRG', 'BRPGA', 'BRPNA'] },
    { name: 'Itajai', portCode: 'BRITJ', altCodes: ['BRITA', 'BRIJA', 'BRIAJ', 'BRITI'] }
  ]
};

/**
 * Service display names
 */
export const SERVICE_NAMES = {
  SAECS: 'SAECS',
  PROTEA: 'PROTEA',
  SAFARI: 'SAFARI',
  MESAWA: 'MESAWA',
  WAFEX: 'WAFEX'
};

/**
 * Vessel IMOs for SAECS service
 * Query each vessel individually to get complete schedule data
 */
export const SAECS_VESSEL_IMOS = [
  '9952751', // ONE Resilience
  '9952775', // ONE Responsibility
  '9952763', // ONE Resolution
  '9430375', // SANTA TERESA
  '9430363', // SANTA ROSA
  '9430387', // SANTA URSULA
  '9400100', // MEHUIN
  '9400083', // MAIPO
  '9400095', // KALAHARI EXPRESS
  '9444742', // SANTA CRUZ
  '9425382', // SANTA RITA
  '9786097'  // MAERSK PANGANI
];

/**
 * Grid settings per service
 */
export const GRID_SETTINGS = {
  VESSELS_PER_GRID: 8,
  DATE_RANGE_DAYS_BACK: 31,
  DATE_RANGE_WEEKS_FORWARD: 24
};

/**
 * Get discharge port configuration for a specific service
 * @param {string} serviceName - Service name (SAECS, PROTEA, etc.)
 * @returns {Array} - Array of discharge port objects
 */
export const getDischargePorts = (serviceName) => {
  return DISCHARGE_PORTS[serviceName] || [];
};

/**
 * Get service code by service name
 * @param {string} serviceName - Service name
 * @returns {string} - Service code or null
 */
export const getServiceCode = (serviceName) => {
  return SERVICE_CODES[serviceName] || null;
};
